import { nanoid } from 'nanoid';
import { isCompNode, isBlockNode, type Sheet, type CompNode, type BlockNode } from './store';
import type { HarnessGraph, HarnessGraphNode } from './types';

export interface HarnessWire {
  edgeId: string;
  wireNo: string;
  fromCompId: string;
  fromComp: string;
  fromPin: string;
  fromConnector: string;
  toCompId: string;
  toComp: string;
  toPin: string;
  toConnector: string;
  gauge: string;
  color: string;
  color2: string;
  label: string;
  netName: string;
  notes: string;
}

export interface HarnessEndpoint {
  /** stable component node id — survives label renames */
  componentId: string;
  component: string;
  connector: string;
  /** number of wires touching this endpoint */
  wireCount: number;
  pinNames: string[];
}

/** Stable endpoint identity: component NODE id + connector group. */
export function endpointKey(componentId: string, connector: string) {
  return `${componentId}||${connector}`;
}

function shouldSwap(aComp: string, bComp: string, aConn: string, bConn: string): boolean {
  const ac = aComp.toLowerCase();
  const bc = bComp.toLowerCase();
  if (ac !== bc) return ac > bc;
  return aConn.toLowerCase() > bConn.toLowerCase();
}

/**
 * Derive harness wires from the SCHEMATIC sheets (the single source of truth
 * for the formboard — block-diagram edges are the abstract view and carry no
 * pins). Keyed by harnessId.
 */
export function computeHarnessWires(sheets: Sheet[]): Map<string, HarnessWire[]> {
  const out = new Map<string, HarnessWire[]>();
  for (const sh of sheets) {
    if (sh.kind !== 'schematic') continue;
    const compIndex = new Map<string, CompNode>();
    const blockIndex = new Map<string, BlockNode>();
    for (const n of sh.nodes) {
      if (isCompNode(n)) compIndex.set(n.id, n);
      else if (isBlockNode(n)) blockIndex.set(n.id, n);
    }
    for (const e of sh.edges) {
      const hid = e.data?.harnessId;
      if (!hid) continue;
      const srcComp = compIndex.get(e.source ?? '');
      const tgtComp = compIndex.get(e.target ?? '');
      const srcBlock = blockIndex.get(e.source ?? '');
      const tgtBlock = blockIndex.get(e.target ?? '');
      const srcPin = srcComp?.data.pins.find((p) => p.id === e.sourceHandle);
      const tgtPin = tgtComp?.data.pins.find((p) => p.id === e.targetHandle);
      const aId = e.source ?? '';
      const bId = e.target ?? '';
      const aComp = srcComp?.data.label ?? srcBlock?.data.title ?? '-- Unknown --';
      const bComp = tgtComp?.data.label ?? tgtBlock?.data.title ?? '-- Unknown --';
      const aPinName = srcPin?.name ?? (srcBlock ? '' : '?');
      const bPinName = tgtPin?.name ?? (tgtBlock ? '' : '?');
      const aConnector = srcPin?.group ?? '';
      const bConnector = tgtPin?.group ?? '';
      const swap = shouldSwap(aComp, bComp, aConnector, bConnector);
      const wire: HarnessWire = {
        edgeId: e.id,
        wireNo: e.data?.wireNo ?? '',
        fromCompId: swap ? bId : aId,
        fromComp: swap ? bComp : aComp,
        fromPin: swap ? bPinName : aPinName,
        fromConnector: swap ? bConnector : aConnector,
        toCompId: swap ? aId : bId,
        toComp: swap ? aComp : bComp,
        toPin: swap ? aPinName : bPinName,
        toConnector: swap ? aConnector : bConnector,
        gauge: e.data?.gauge ?? '',
        color: e.data?.color ?? '',
        color2: e.data?.color2 ?? '',
        label: e.data?.label ?? '',
        netName: (e.data?.netName ?? '').trim() || (e.data?.label ?? ''),
        notes: e.data?.notes ?? '',
      };
      const list = out.get(hid);
      if (list) list.push(wire);
      else out.set(hid, [wire]);
    }
  }
  return out;
}

export function computeHarnessEndpoints(
  wiresByHarness: Map<string, HarnessWire[]>,
): Map<string, HarnessEndpoint[]> {
  const out = new Map<string, HarnessEndpoint[]>();
  const naturalSort = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  for (const [hid, wires] of wiresByHarness.entries()) {
    const epMap = new Map<
      string,
      { componentId: string; component: string; connector: string; pins: Set<string>; wires: number }
    >();
    const record = (componentId: string, component: string, connector: string, pin: string) => {
      const key = endpointKey(componentId, connector);
      const ex = epMap.get(key);
      if (ex) {
        ex.wires += 1;
        if (pin) ex.pins.add(pin);
      } else {
        epMap.set(key, { componentId, component, connector, pins: new Set(pin ? [pin] : []), wires: 1 });
      }
    };
    for (const w of wires) {
      record(w.fromCompId, w.fromComp, w.fromConnector, w.fromPin);
      record(w.toCompId, w.toComp, w.toConnector, w.toPin);
    }
    const list: HarnessEndpoint[] = Array.from(epMap.values()).map((ep) => ({
      componentId: ep.componentId,
      component: ep.component,
      connector: ep.connector,
      wireCount: ep.wires,
      pinNames: Array.from(ep.pins).sort(naturalSort),
    }));
    list.sort((a, b) => b.wireCount - a.wireCount);
    out.set(hid, list);
  }
  return out;
}

/** Does a graph connector node correspond to this schematic endpoint? Uses the
 *  stable node-id link when present, else falls back to the label (legacy). */
function nodeMatchesEndpoint(node: HarnessGraphNode, ep: HarnessEndpoint): boolean {
  if (node.refNodeId) return node.refNodeId === ep.componentId && (node.refConnector ?? '') === ep.connector;
  return (node.refComponent ?? '') === ep.component && (node.refConnector ?? '') === ep.connector;
}

function mkConnector(ep: HarnessEndpoint): HarnessGraphNode {
  return {
    id: nanoid(8),
    type: 'connector',
    x: 0,
    y: 0,
    label: ep.connector || ep.component,
    refNodeId: ep.componentId,
    refComponent: ep.component,
    refConnector: ep.connector,
  };
}

/** Find the metadata node (termination / contact PNs) for a live schematic
 *  endpoint, if one was ever recorded. The connector LIST itself is always
 *  the live `endpoints` — this is only a sidecar for the extra fields the
 *  schematic doesn't carry. */
export function findConnectorNode(graph: HarnessGraph, ep: HarnessEndpoint): HarnessGraphNode | undefined {
  return graph.nodes.find((n) => n.type === 'connector' && nodeMatchesEndpoint(n, ep));
}

/** Create-or-update the metadata node for an endpoint (termination / contact
 *  PN edits). No manual add/sync step needed — the node is materialized the
 *  first time the user types something for that connector. */
export function upsertConnectorNode(
  graph: HarnessGraph,
  ep: HarnessEndpoint,
  patch: Partial<Pick<HarnessGraphNode, 'termination' | 'contactPNs'>>,
): HarnessGraph {
  const existing = findConnectorNode(graph, ep);
  if (existing) {
    return { ...graph, nodes: graph.nodes.map((n) => (n.id === existing.id ? { ...n, ...patch } : n)) };
  }
  return { ...graph, nodes: [...graph.nodes, { ...mkConnector(ep), ...patch }] };
}
